import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Modal,
  Form,
  InputGroup,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  Book,
  Mortarboard,
  Eye,
  Download,
  CalendarEvent,
  FileEarmarkText,
  CheckCircleFill,
  XCircleFill,
  Clock,
  SendCheck,
  CardChecklist,
  PersonVcard,
  Bullseye,
  HourglassTop,
  PencilSquare,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";

import LOGO_URL from "../img/tmis_logo_orig.png";
import EDIT_PROFILE_ICON from "../img/profile/edit-profile.png";
import FORGOT_PASSWORD_ICON from "../img/profile/forgot-password.png";
import RENEWAL_LICENSE_ICON from "../img/profile/renewal-license.png";
import LICENSE_BG from "../img/profile/license-sample.png";
import LicenseRenewalModal from "../components/LicenseRenewalModal.js";

const API_BASE = process.env.REACT_APP_API || "";

const ENDPOINT_TG_LIST = `${API_BASE}/getTGByList`;
const ENDPOINT_ACTIVITY = `${API_BASE}/getActivityByVerifiedUser`;
const ENDPOINT_UPDATE_USER = `${API_BASE}/updateUser`;
const ENDPOINT_ACTIVITIES_BY_USER = `${API_BASE}/getActivitiesByUser`;
const ENDPOINT_ACTIVITY_BY_ID = `${API_BASE}/getActivityById`;
const ENDPOINT_DOWNLOAD_SINGLE = `${API_BASE}/downloadSingleDocument`;
const ENDPOINT_ANALYZE_BADGE = `${API_BASE}/analyzeFile`;
const ENDPOINT_UPDATE_LICENCE = `${API_BASE}/userUpdateLicence`;
const ENDPOINT_GET_UPDATE_EXPIRY = `${API_BASE}/getUserUpdateExpiry`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const CURRENT_YEAR = new Date().getFullYear();

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getAuthToken() {
  const keys = ["token", "authToken", "auth_token"];
  for (const k of keys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) return v;
  }
  return "";
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";

  const email =
    user.email || user.email_address || localStorage.getItem("email") || "";
  const badgeNo = user.badge_no || "";
  const id = user.user_id ?? user.id ?? "";

  return { displayName, email, badgeNo, id };
}

function getInitials(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    (parts[0].charAt(0) || "").toUpperCase() +
    (parts[parts.length - 1].charAt(0) || "").toUpperCase()
  );
}

function formatExpiryDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return String(dateString).toUpperCase();
  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, " ")
    .toUpperCase();
}

function prettyProfileDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTrackingDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (/^\d{2}-\d{2}-\d{4}/.test(raw)) return raw;

  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  return raw;
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase();
}

function isFinalRenewalStatus(status) {
  const s = normalizeStatus(status);
  return s.includes("approve") || s.includes("reject");
}

function isRejectedRenewalStatus(status) {
  return normalizeStatus(status).includes("reject");
}

function isApprovedRenewalStatus(status) {
  return normalizeStatus(status).includes("approve");
}

function isUnderReviewRenewalStatus(status) {
  const s = normalizeStatus(status);
  return (
    s === "under review" ||
    s === "reviewing" ||
    s.includes("under review") ||
    s.includes("review")
  );
}

function fileSizeLabel(bytes) {
  const n = Number(bytes || 0);
  if (!n) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function badgeVariantByStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("approve")) return "success";
  if (s.includes("reject")) return "danger";
  if (s.includes("pending") || s.includes("submit") || s.includes("resubmit")) {
    return "warning";
  }
  return "secondary";
}

function statusIcon(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("approve")) return <CheckCircleFill className="me-1" />;
  if (s.includes("reject")) return <XCircleFill className="me-1" />;
  if (s.includes("pending") || s.includes("submit") || s.includes("resubmit")) {
    return <Clock className="me-1" />;
  }
  return <FileEarmarkText className="me-1" />;
}

const SUBMISSION_STATUS_STYLES = {
  Pending: {
    bg: "rgba(245,158,11,.14)",
    text: "#B45309",
    icon: <Clock size={13} />,
  },
  Approved: {
    bg: "rgba(16,185,129,.14)",
    text: "#047857",
    icon: <CheckCircleFill size={13} />,
  },
  Rejected: {
    bg: "rgba(239,68,68,.14)",
    text: "#B91C1C",
    icon: <XCircleFill size={13} />,
  },
  "Re-submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={13} />,
  },
  Draft: {
    bg: "rgba(139,92,246,.14)",
    text: "#6D28D9",
    icon: <PencilSquare size={13} />,
  },
  Default: {
    bg: "rgba(148,163,184,.16)",
    text: "#475569",
    icon: <FileEarmarkText size={13} />,
  },
};

function submissionStatusPillStyle(status) {
  const { bg, text } =
    SUBMISSION_STATUS_STYLES[status] || SUBMISSION_STATUS_STYLES.Default;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    background: bg,
    color: text,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}

function basenameFromPath(p) {
  if (!p) return "";
  const clean = String(p).replace(/\\/g, "/");
  const parts = clean.split("/");
  return parts[parts.length - 1] || "";
}

const COUNTRY_CODES = [
  { label: "+65 (SG)", value: "+65" },
  { label: "+95 (MM)", value: "+95" },
  { label: "+60 (MY)", value: "+60" },
  { label: "+66 (TH)", value: "+66" },
  { label: "+84 (VN)", value: "+84" },
  { label: "+91 (IN)", value: "+91" },
];

function prettyAttachType(t) {
  const map = {
    ccNoOfAttach: "Classroom Declaration",
    vlAttached: "Virtual Learning",
    sstAttached: "Site-Specific Training",
    ppAttached: "Proof of Participation",
    remNoOfAttach: "Proof of Remuneration",
    conNoOfAttach: "Proof of Tour Conducted",
  };
  return map[t] || t || "-";
}

function ActionImageButton({ image, label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        border: disabled ? "1px solid #E5E7EB" : "1px solid #BFD2F8",
        background: disabled ? "#F9FAFB" : "#FFFFFF",
        borderRadius: 12,
        width: "100%",
        minHeight: 74,
        padding: "8px 6px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "0.2s ease",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <img
        src={image}
        alt={label}
        style={{
          width: 29,
          height: 29,
          objectFit: "contain",
          filter: disabled ? "grayscale(100%) brightness(85%)" : "none",
        }}
      />

      <span
        style={{
          fontSize: 12,
          lineHeight: 1.15,
          color: disabled ? "#9CA3AF" : "#3373D9",
          fontWeight: 700,
          textAlign: "center",
          whiteSpace: "pre-line",
        }}
      >
        {label}
      </span>
    </button>
  );
}

function LicensePreviewCard({ name, expiryDate, badgeNo }) {
  const isMobile = window.innerWidth < 768;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "0.82",
        borderRadius: 14,
        overflow: "hidden",
        background: "#fff",
        border: "1px solid #DCE3ED",
      }}
    >
      <img
        src={LICENSE_BG}
        alt="Sample licence"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: "18.5%",
          left: "70%",
          transform: "translateX(-50%)",
          width: "45%",
          textAlign: "center",
          fontSize: isMobile
            ? "clamp(14px, 4vw, 18px)"
            : "clamp(10px, 1vw, 17px)",
          fontWeight: 500,
          color: "#111",
          lineHeight: 1.2,
          textTransform: "uppercase",
        }}
      >
        {name || "-"}
      </div>

      <div
        style={{
          position: "absolute",
          top: "30.5%",
          left: "65%",
          transform: "translateX(-50%)",
          width: "60%",
          textAlign: "center",
          fontSize: isMobile
            ? "clamp(7px, 3vw, 9px)"
            : "clamp(7px, 0.72vw, 9px)",
          fontWeight: 700,
          color: "#111",
          textTransform: "uppercase",
        }}
      >
        IS LICENSED TO GUIDE TOURIST IN
      </div>

      <div
        style={{
          position: "absolute",
          top: "38%",
          left: "67%",
          transform: "translateX(-50%)",
          width: "44%",
          textAlign: "center",
          fontSize: isMobile
            ? "clamp(15px, 4vw, 22px)"
            : "clamp(10px, 1.2vw, 19px)",
          fontWeight: 500,
          color: "#111",
          textTransform: "uppercase",
        }}
      >
        English
      </div>

      <div
        style={{
          position: "absolute",
          left: "5%",
          bottom: "30%",
          fontSize: isMobile
            ? "clamp(16px, 4vw, 22px)"
            : "clamp(16px, 0.95vw, 25px)",
          fontWeight: 700,
          color: "#111",
          textTransform: "uppercase",
          letterSpacing: "0.03em",
        }}
      >
        {badgeNo || "-"}
      </div>

      <div
        style={{
          position: "absolute",
          right: "5%",
          bottom: "5%",
          fontSize: isMobile
            ? "clamp(18px, 5vw, 28px)"
            : "clamp(16px, 1.85vw, 30px)",
          fontWeight: 500,
          color: "#111",
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {formatExpiryDate(expiryDate)}
      </div>
    </div>
  );
}

function TrackingStep({
  title,
  date,
  icon,
  active,
  lineActive,
  isLast,
  statusType = "normal",
}) {
  const isRejected = statusType === "rejected";

  const activeBg = isRejected
    ? "linear-gradient(135deg, #DC2626 0%, #FB7185 100%)"
    : "linear-gradient(135deg, #2F80ED 0%, #56CCF2 100%)";

  const lineBg = isRejected
    ? "linear-gradient(90deg, #DC2626 0%, #FB7185 100%)"
    : "linear-gradient(90deg, #2F80ED 0%, #56CCF2 100%)";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: active ? activeBg : "#E9EEF5",
            color: active ? "#fff" : "#90A0B7",
            boxShadow: active
              ? isRejected
                ? "0 10px 20px rgba(220,38,38,0.22)"
                : "0 10px 20px rgba(47,128,237,0.22)"
              : "none",
            border: active ? "none" : "1px solid #D7DFEA",
            zIndex: 2,
          }}
        >
          {icon}
        </div>
      </div>

      {!isLast && (
        <div
          style={{
            flex: 1,
            height: 4,
            marginTop: 25,
            background: lineActive ? lineBg : "#E9EEF5",
            borderRadius: 999,
            marginLeft: 10,
            marginRight: 10,
          }}
        />
      )}

      <div
        style={{
          position: "absolute",
          marginTop: 66,
          transform: "translateX(-8px)",
          maxWidth: 210,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            color: "#1F2A37",
            lineHeight: 1.3,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: date ? (isRejected ? "#B91C1C" : "#4B5563") : "#9CA3AF",
            marginTop: 4,
            fontWeight: isRejected ? 700 : 400,
          }}
        >
          {date || "Pending"}
        </div>
      </div>
    </div>
  );
}

function ColorStatCard({ title, value, icon, gradient, subtitle }) {
  return (
    <Card
      className="h-100"
      style={{
        borderRadius: 18,
        border: "none",
        overflow: "hidden",
        background: gradient,
        boxShadow: "0 12px 28px rgba(16, 24, 40, 0.10)",
      }}
    >
      <Card.Body style={{ padding: 18, color: "#fff" }}>
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                opacity: 0.95,
                letterSpacing: 0.5,
              }}
            >
              {title}
            </div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 900,
                lineHeight: 1.05,
                marginTop: 10,
              }}
            >
              {value}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 12, opacity: 0.92, marginTop: 8 }}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 16,
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(4px)",
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { displayName, email, badgeNo, id } = useMemo(getStoredUser, []);
  const authToken = useMemo(getAuthToken, []);

  const [profile, setProfile] = useState({
    userId: id,
    firstName: "",
    lastName: "",
    fullName: displayName,
    email,
    badgeNo,
    countryCode: "",
    phoneNo: "",
    username: "",
    tgExpiryDate: "",
    statusName: "",
    nonLicenceExpiryDate: "",
    isVerified: 0,
  });

  const [stats, setStats] = useState({ learn: 0, do: 0, courses: 0 });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [form, setForm] = useState({
    userId: id,
    username: email || "",
    firstName: "",
    lastName: "",
    countryCode: "",
    phoneNo: "",
    tgExpiryDate: "",
    badgeNo: "",
  });

  const [showRenewal, setShowRenewal] = useState(false);
  const [renewalTracking, setRenewalTracking] = useState({
    submittedDate: "",
    reviewingDate: "",
    decisionDate: "",
  });
  const [renewalEditData, setRenewalEditData] = useState(null);
  const [updateExpiryRows, setUpdateExpiryRows] = useState([]);
  const [latestUpdateExpiry, setLatestUpdateExpiry] = useState(null);
  const [loadingUpdateExpiry, setLoadingUpdateExpiry] = useState(false);
  const [updateExpiryError, setUpdateExpiryError] = useState("");

  const [showExpirySubmission, setShowExpirySubmission] = useState(false);
  const [expirySubmission, setExpirySubmission] = useState(null);
  const [expiryFileUrl, setExpiryFileUrl] = useState("");
  const [loadingExpirySubmission, setLoadingExpirySubmission] = useState(false);
  const [expirySubmissionError, setExpirySubmissionError] = useState("");
  const [cancellingExpiry, setCancellingExpiry] = useState(false);

  const [year, setYear] = useState(CURRENT_YEAR);
  const [learnList, setLearnList] = useState([]);
  const [doList, setDoList] = useState([]);
  const [loadingLearn, setLoadingLearn] = useState(false);
  const [loadingDo, setLoadingDo] = useState(false);
  const [subError, setSubError] = useState("");

  const [showDetail, setShowDetail] = useState(false);
  const [detailModule, setDetailModule] = useState("learn");
  const [detailId, setDetailId] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailDocs, setDetailDocs] = useState([]);
  const [viewingDocKey, setViewingDocKey] = useState(null);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!authToken) handleLogout();
  }, [authToken, handleLogout]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const openEditModal = () => {
    setEditError("");
    setEditSuccess("");
    setForm((p) => ({
      ...p,
      userId: profile.userId || id,
      username: profile.username || profile.email || email || "",
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      countryCode: profile.countryCode || "",
      phoneNo: profile.phoneNo || "",
      tgExpiryDate: profile.tgExpiryDate || "",
      badgeNo: profile.badgeNo || badgeNo || "",
    }));
    setShowEdit(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setShowEdit(false);
  };

  const openRenewalModal = () => {
    setRenewalEditData(latestUpdateExpiry || null);
    setShowRenewal(true);
  };

  const closeRenewalModal = () => {
    setShowRenewal(false);
  };

  const closeExpirySubmissionModal = () => {
    if (loadingExpirySubmission || cancellingExpiry) return;
    setShowExpirySubmission(false);
  };

  const openExpirySubmissionModal = async (record = latestUpdateExpiry) => {
    const ebId = record?.ebId || record?.id;
    if (!ebId) {
      setExpirySubmissionError("No update expiry submission found.");
      setShowExpirySubmission(true);
      return;
    }

    setShowExpirySubmission(true);
    setLoadingExpirySubmission(true);
    setExpirySubmissionError("");
    setExpirySubmission(null);
    setExpiryFileUrl("");

    try {
      const { data } = await axios.get(ENDPOINT_GET_UPDATE_EXPIRY, {
        params: { id: ebId },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 20000,
      });

      if (data?.success) {
        const row = Array.isArray(data.data) ? data.data[0] : null;
        setExpirySubmission(row || null);
        setExpiryFileUrl(data?.key || "");
        if (!row) setExpirySubmissionError("Submission not found.");
      } else {
        setExpirySubmissionError(data?.message || "Unable to load submission.");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }
      setExpirySubmissionError(
        e?.response?.data?.message || "Unable to load submission.",
      );
    } finally {
      setLoadingExpirySubmission(false);
    }
  };

  const cancelExpirySubmission = async () => {
    const ebId = expirySubmission?.ebId || latestUpdateExpiry?.ebId;
    if (!ebId || !isUnderReviewRenewalStatus(expirySubmission?.status)) return;

    const ok = window.confirm(
      "Are you sure you want to cancel this update expiry date submission?",
    );
    if (!ok) return;

    try {
      setCancellingExpiry(true);
      setExpirySubmissionError("");

      const { data } = await axios.post(
        ENDPOINT_UPDATE_LICENCE,
        {
          id: ebId,
          refNumber:
            expirySubmission?.ref_number || latestUpdateExpiry?.ref_number || "",
          badgeNo: profile.badgeNo || expirySubmission?.badge_no || "",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          timeout: 20000,
        },
      );

      if (data?.success) {
        await loadUpdateExpiryTracking();
        await openExpirySubmissionModal({ ebId });
      } else {
        setExpirySubmissionError(data?.message || "Unable to cancel submission.");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }
      setExpirySubmissionError(
        e?.response?.data?.message || "Unable to cancel submission.",
      );
    } finally {
      setCancellingExpiry(false);
    }
  };

  const loadProfile = useCallback(async () => {
    if (!API_BASE || !authToken) return;

    try {
      setLoadingProfile(true);
      setError("");

      const onlyUserId = id || getStoredUser()?.id || "";

      if (!onlyUserId) {
        setError("User ID not found.");
        setLoadingProfile(false);
        return;
      }

      const { data } = await axios.get(ENDPOINT_TG_LIST, {
        params: {
          type: "detail",
          isAdmin: null,
          userId: ` AND u.user_id = ${onlyUserId}`,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
        const d = data.data[0];

        const firstName = d.first_name || "";
        const lastName = d.last_name || "";
        const fullName =
          `${firstName} ${lastName}`.trim() || d.username || displayName;

        setProfile({
          userId: d.user_id ?? id,
          firstName,
          lastName,
          fullName,
          email: d.email || email,
          badgeNo: d.badge_no || badgeNo,
          countryCode: d.country_code || "",
          phoneNo: d.phone_number || "",
          username: d.username || d.email || email || "",
          tgExpiryDate: d.tg_expiry_date || "",
          statusName: d.status_name || "",
          nonLicenceExpiryDate: d.nonLicenceExpiryDate || "",
          isVerified: Number(d.isVerified || 0),
        });

        setForm((prev) => ({
          ...prev,
          userId: d.user_id ?? id,
          username: d.username || d.email || email || "",
          firstName,
          lastName,
          countryCode: d.country_code || "",
          phoneNo: d.phone_number || "",
          tgExpiryDate: d.tg_expiry_date || "",
          badgeNo: d.badge_no || badgeNo || "",
        }));
      } else {
        setError("Profile not found.");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }
      setError("Unable to load profile at the moment.");
    } finally {
      setLoadingProfile(false);
    }
  }, [authToken, badgeNo, displayName, email, id, handleLogout]);

  const loadStats = useCallback(async () => {
    if (!API_BASE || !authToken) return;
    try {
      setLoadingStats(true);
      const { data } = await axios.get(ENDPOINT_ACTIVITY, {
        params: { userId: id, year: CURRENT_YEAR },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data.data) && data.data.length) {
        const d = data.data[0];
        setStats({
          learn: Number(d.ctrLearn || 0),
          do: Number(d.ctrDo || 0),
          courses: Number(d.ctrCourses || 0),
        });
      } else {
        setStats({ learn: 0, do: 0, courses: 0 });
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }
      setStats({ learn: 0, do: 0, courses: 0 });
    } finally {
      setLoadingStats(false);
    }
  }, [authToken, id, handleLogout]);

  const loadUpdateExpiryTracking = useCallback(async () => {
    if (!API_BASE || !authToken) return;

    try {
      setLoadingUpdateExpiry(true);
      setUpdateExpiryError("");

      const { data } = await axios.get(ENDPOINT_GET_UPDATE_EXPIRY, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 20000,
      });

      let rows = Array.isArray(data?.data) ? data.data : [];
      const currentUserId = String(profile.userId || id || "");

      rows = rows.filter((r) => String(r.user_id || "") === currentUserId);

      const sortedRows = [...rows].sort((a, b) => {
        return Number(b.ebId || 0) - Number(a.ebId || 0);
      });

      setUpdateExpiryRows(sortedRows);

      const latest = sortedRows[0] || null;
      setLatestUpdateExpiry(latest);

      if (!latest) {
        setRenewalTracking({
          submittedDate: "",
          reviewingDate: "",
          decisionDate: "",
        });
        return;
      }

      const submittedDate = formatTrackingDate(
        latest.uploaded_date || latest.createdDate || "",
      );

      const reviewingDate = submittedDate;

      const decisionDate = isFinalRenewalStatus(latest.status)
        ? formatTrackingDate(latest.approved_date || "")
        : "";

      setRenewalTracking({
        submittedDate,
        reviewingDate,
        decisionDate,
      });
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }

      setUpdateExpiryRows([]);
      setLatestUpdateExpiry(null);
      setRenewalTracking({
        submittedDate: "",
        reviewingDate: "",
        decisionDate: "",
      });
      setUpdateExpiryError(
        e?.response?.data?.message || "Unable to load update expiry tracking.",
      );
    } finally {
      setLoadingUpdateExpiry(false);
    }
  }, [authToken, handleLogout, id, profile.userId]);

  const loadSubmissions = useCallback(
    async (y) => {
      if (!API_BASE || !authToken) return;
      const yr = Number(y || year || CURRENT_YEAR) || CURRENT_YEAR;

      setSubError("");
      setLoadingLearn(true);
      setLoadingDo(true);

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };

      try {
        const [learnRes, doRes] = await Promise.allSettled([
          axios.get(ENDPOINT_ACTIVITIES_BY_USER, {
            params: { module: "learn", year: yr },
            headers,
            timeout: 20000,
          }),
          axios.get(ENDPOINT_ACTIVITIES_BY_USER, {
            params: { module: "do", year: yr },
            headers,
            timeout: 20000,
          }),
        ]);

        setLearnList(
          learnRes.status === "fulfilled" &&
            Array.isArray(learnRes.value?.data?.data)
            ? learnRes.value.data.data
            : [],
        );
        setDoList(
          doRes.status === "fulfilled" && Array.isArray(doRes.value?.data?.data)
            ? doRes.value.data.data
            : [],
        );
      } catch {
        setLearnList([]);
        setDoList([]);
        setSubError("Unable to load submissions.");
      } finally {
        setLoadingLearn(false);
        setLoadingDo(false);
      }
    },
    [authToken, year],
  );

  useEffect(() => {
    if (!authToken) return;
    loadProfile();
    loadStats();
  }, [authToken, loadProfile, loadStats]);

  useEffect(() => {
    if (!authToken) return;
    loadSubmissions(year);
  }, [authToken, year, loadSubmissions]);

  useEffect(() => {
    if (!authToken || !profile.userId) return;
    loadUpdateExpiryTracking();
  }, [authToken, profile.userId, loadUpdateExpiryTracking]);

  const initials = getInitials(profile.fullName);

  const onChange = (key) => (e) => {
    const v = e?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const validate = () => {
    if (!(form.firstName || "").trim()) return "First name is required.";
    if (!(form.lastName || "").trim()) return "Last name is required.";
    const emailVal = (form.username || "").trim();
    if (!emailVal) return "Email is required.";
    if (!/^\S+@\S+\.\S+$/.test(emailVal))
      return "Please enter a valid email address.";
    if ((form.phoneNo || "").trim().length < 5)
      return "Please enter a valid phone number.";
    return "";
  };

  const saveProfile = async () => {
    const msg = validate();
    if (msg) {
      setEditError(msg);
      setEditSuccess("");
      return;
    }

    setSaving(true);
    setEditError("");
    setEditSuccess("");

    const payload = {
      userId: String(form.userId || profile.userId || id),
      username: (form.username || "").trim(),
      firstName: (form.firstName || "").trim(),
      lastName: (form.lastName || "").trim(),
      countryCode: (form.countryCode || profile.countryCode || "").trim(),
      phoneNo: (form.phoneNo || "").trim(),
    };

    try {
      const { data } = await axios.post(ENDPOINT_UPDATE_USER, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 20000,
      });

      if (data?.success) {
        setEditSuccess("Updated successfully.");
        await loadProfile();
        setShowEdit(false);
      } else {
        setEditError(data?.message || "Update failed. Please try again.");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }
      setEditError(
        e?.response?.data?.message || "Server error. Please try again later.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (module, activityId) => {
    if (!activityId) return;
    setShowDetail(true);
    setDetailModule(module);
    setDetailId(String(activityId));
    setDetailDocs([]);
    setDetailError("");
    setDetailLoading(true);

    try {
      const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
        params: { module, id: activityId },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        timeout: 20000,
      });

      if (data?.success) {
        setDetailDocs(Array.isArray(data?.docs) ? data.docs : []);
      } else {
        setDetailError(data?.message || "Unable to load details.");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        handleLogout();
        return;
      }
      setDetailError(e?.response?.data?.message || "Unable to load details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    if (detailLoading) return;
    setShowDetail(false);
  };

  const getDocFilename = (doc) => {
    const name = (doc?.file_name || "").trim();
    if (name) return name;
    return (basenameFromPath(doc?.file_path) || "").trim();
  };

  const downloadDoc = async (doc) => {
    const filename = getDocFilename(doc);
    const activityId = String(detailId || "").trim();
    const userIdToSend = String(
      profile.userId || form.userId || id || "",
    ).trim();

    if (!userIdToSend || !filename || !activityId) {
      alert("Download information is missing.");
      return;
    }

    try {
      const params = new URLSearchParams();
      if (detailModule === "learn") params.set("learnId", activityId);
      else params.set("doId", activityId);

      params.set("filename", filename);
      params.set("isAdmin", "1");
      params.set("user_id", userIdToSend);

      const res = await axios.get(
        `${ENDPOINT_DOWNLOAD_SINGLE}?${params.toString()}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 60000,
        },
      );

      const blob = new Blob([res.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(
        e?.response?.status === 404
          ? `File not found: ${filename}`
          : "Download failed. Please try again.",
      );
    }
  };

  const guessMimeFromName = (filename = "") => {
    const ext = String(filename).toLowerCase().split(".").pop();
    if (ext === "pdf") return "application/pdf";
    if (ext === "png") return "image/png";
    if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
    if (ext === "gif") return "image/gif";
    if (ext === "webp") return "image/webp";
    if (ext === "heic" || ext === "heif") return "image/heic";
    return "application/octet-stream";
  };

  const viewDoc = async (doc) => {
    const filename = getDocFilename(doc);
    const activityId = String(detailId || "").trim();
    const userIdToSend = String(
      profile.userId || form.userId || id || "",
    ).trim();

    if (!userIdToSend || !filename || !activityId) {
      alert("File information is missing.");
      return;
    }

    const previewWin = window.open("about:blank", "_blank");
    if (!previewWin) {
      alert("Popup blocked. Please allow popups for this site to preview files.");
      return;
    }
    try {
      previewWin.opener = null;
    } catch {}

    const docKey = doc?.doc_id ?? `${filename}-${doc?.uploaded_date}`;
    setViewingDocKey(docKey);

    try {
      const params = new URLSearchParams();
      if (detailModule === "learn") params.set("learnId", activityId);
      else params.set("doId", activityId);
      params.set("filename", filename);
      params.set("isAdmin", "1");
      params.set("user_id", userIdToSend);

      const res = await axios.get(
        `${ENDPOINT_DOWNLOAD_SINGLE}?${params.toString()}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 60000,
        },
      );

      const mime = guessMimeFromName(filename);
      const blobUrl = window.URL.createObjectURL(
        new Blob([res.data], { type: mime }),
      );
      previewWin.location.href = blobUrl;
    } catch (e) {
      previewWin.close();
      alert(
        e?.response?.status === 404
          ? `File not found: ${filename}`
          : "Unable to preview this file. Please try downloading it instead.",
      );
    } finally {
      setViewingDocKey(null);
    }
  };

  const groupDocsByType = useMemo(() => {
    const groups = {};
    for (const d of detailDocs || []) {
      const key = d.attachment_type || "unknown";
      groups[key] = groups[key] || [];
      groups[key].push(d);
    }
    return groups;
  }, [detailDocs]);

  const years = useMemo(() => {
    const minYear = 2026;
    const cur = new Date().getFullYear();
    const maxYear = Math.max(cur, minYear);
    const arr = [];
    for (let y = maxYear; y >= minYear; y -= 1) arr.push(y);
    return arr;
  }, []);

  function isCancelledRenewalStatus(status) {
  const s = normalizeStatus(status);
  return s.includes("cancel");
}

  const latestStatus = latestUpdateExpiry?.status || "";

const rejected = isRejectedRenewalStatus(latestStatus);
const approved = isApprovedRenewalStatus(latestStatus);
const cancelled = isCancelledRenewalStatus(latestStatus);

const finalDone = rejected || approved || cancelled;

  const trackingSteps = [
  {
    title: "Update Expiry Date Submitted",
    date: renewalTracking.submittedDate,
    icon: <SendCheck size={24} />,
  },
  {
    title: cancelled ? "Cancelled" : "Under Review",
    date: cancelled
      ? renewalTracking.reviewingDate
      : renewalTracking.reviewingDate,
    icon: cancelled ? (
      <XCircleFill size={24} />
    ) : (
      <HourglassTop size={24} />
    ),
  },
  {
    title: rejected
      ? "Rejected"
      : approved
        ? "Approved"
        : cancelled
          ? "Cancelled"
          : "Approved / Rejected",
    date:
      approved || rejected
        ? renewalTracking.decisionDate || "Completed"
        : "",
    icon: rejected ? (
      <XCircleFill size={24} />
    ) : approved ? (
      <CheckCircleFill size={24} />
    ) : cancelled ? (
      <XCircleFill size={24} />
    ) : (
      <CheckCircleFill size={24} />
    ),
  },
];

 const lastActiveIndex = cancelled
  ? 1
  : finalDone
    ? 2
    : renewalTracking.reviewingDate
      ? 1
      : renewalTracking.submittedDate
        ? 0
        : -1;

  const SubmissionCard = ({ module, rowData }) => {
    const isLearn = module === "learn";
    const activityId = isLearn ? rowData?.learn_id : rowData?.do_id;
    const title = isLearn
      ? rowData?.typeOfActivity || rowData?.activity_name || "Learn Activity"
      : rowData?.activity_name || "Do Activity";

    const dateLine = `${rowData?.actDateFr || "-"} - ${rowData?.actDateTo || "-"}`;
    const status = rowData?.status_name || "-";
    const activityName = rowData?.activity_name || "";
    const activityProvider = rowData?.activity_provider || "";
    const remarks = rowData?.remarks || "";
    const hasConductedTour =
      !isLearn && typeof rowData?.hasConductedTour !== "undefined"
        ? Boolean(Number(rowData.hasConductedTour))
        : null;

    const accent = isLearn
      ? {
          border: "#b9b9b9",
          iconBg: "linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)",
        }
      : {
          border: "#b9b9b9",
          iconBg: "linear-gradient(135deg, #FF6A57 0%, #f88677 100%)",
        };

    return (
      <Card
        className="h-100"
        style={{
          borderRadius: 18,
          border: `1px solid ${accent.border}`,
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
          overflow: "hidden",
          transition: "transform .15s ease, box-shadow .15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 12px 26px rgba(15, 23, 42, 0.10)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "none";
          e.currentTarget.style.boxShadow = "0 8px 20px rgba(15, 23, 42, 0.06)";
        }}
      >
        <Card.Body style={{ padding: 16 }}>
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 12,
                    background: accent.iconBg,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {isLearn ? <Book size={18} /> : <Bullseye size={18} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{ fontWeight: 900, fontSize: 15, color: "#1F2937" }}
                  >
                    {title}
                  </div>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>
                    {dateLine}
                  </div>
                </div>
              </div>

              {(activityName || activityProvider) && (
                <div
                  className="mt-2"
                  style={{
                    fontSize: 12,
                    color: "#475467",
                    background: "rgba(255,255,255,0.65)",
                    borderRadius: 14,
                    padding: "10px 12px",
                  }}
                >
                  {activityName && (
                    <div>
                      <span style={{ fontWeight: 800 }}>Activity Name:</span>{" "}
                      {activityName}
                    </div>
                  )}
                  {activityProvider && (
                    <div className="mt-1">
                      <span style={{ fontWeight: 800 }}>
                        Activity Provider:
                      </span>{" "}
                      {activityProvider}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
                <span style={submissionStatusPillStyle(status)}>
                  {(
                    SUBMISSION_STATUS_STYLES[status] ||
                    SUBMISSION_STATUS_STYLES.Default
                  ).icon}
                  {status}
                </span>

                {hasConductedTour !== null && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: hasConductedTour
                        ? "rgba(16,185,129,.14)"
                        : "rgba(239,68,68,.14)",
                      color: hasConductedTour ? "#047857" : "#B91C1C",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {hasConductedTour ? (
                      <CheckCircleFill size={13} />
                    ) : (
                      <XCircleFill size={13} />
                    )}
                    {hasConductedTour ? "Conducted Tour" : "No Tour"}
                  </span>
                )}
              </div>
            </div>

            <Button
              size="sm"
              variant="primary"
              onClick={() => openDetail(module, activityId)}
              style={{
                borderRadius: 12,
                border: "1px solid rgba(15,23,42,0.08)",
                whiteSpace: "nowrap",
                fontWeight: 700,
              }}
            >
              <Eye className="me-1" />
              View Documents
            </Button>
          </div>

          {remarks && (
            <div
              style={{
                padding: "12px 14px",
                marginTop: 10,
                background: "#FFFBEA",
                borderRadius: 12,
                border: "1px solid #F3E7A6",
                fontSize: 12,
              }}
            >
              <span style={{ fontWeight: 800, color: "#16386D" }}>
                Remarks:
              </span>
              <span
                style={{ marginLeft: 8, color: "#16386D", fontWeight: 500 }}
              >
                {remarks}
              </span>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const handleRenewalSubmitted = async () => {
    await loadProfile();
    await loadUpdateExpiryTracking();
  };

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: "#f7f9fc" }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
            />
            <span
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className="custom-font"
              onClick={() => navigate("/profile")}
            >
              {displayName}
            </span>
            <PersonCircle
              size={23}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/profile")}
            />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center"
            style={{ gap: 12 }}
          >
            <span
              role="button"
              tabIndex={0}
              onClick={handleBack}
              style={{ cursor: "pointer" }}
            >
              <ArrowLeft size={18} color="#ffffff" />
            </span>
            <h4 className="m-0 fw-bold">PROFILE</h4>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        <Row className="g-4">
          <Col xs={12} md={4} lg={3}>
            <div className="d-flex flex-column gap-3">
              <Card
                style={{
                  background: PANEL_BG,
                  borderRadius: 18,
                  border: "1px solid #E6EAF0",
                }}
              >
                <Card.Body
                  style={{
                    background: "white",
                    borderRadius: 16,
                    paddingTop: 16,
                    paddingBottom: 12,
                  }}
                >
                  <div className="d-flex flex-column align-items-center mb-3">
                    <div
                      style={{
                        width: 74,
                        height: 74,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #F4CF57 0%, #FFB347 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 28,
                        fontWeight: 700,
                        color: "#333",
                        marginBottom: 10,
                        boxShadow: "0 10px 22px rgba(244,207,87,0.26)",
                      }}
                    >
                      {initials}
                    </div>

                    <div
                      style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}
                    >
                      {profile.fullName}
                    </div>

                    {profile.badgeNo && (
                      <div
                        style={{
                          background: "#00B894",
                          borderRadius: 999,
                          padding: "2px 12px",
                          color: "white",
                          fontSize: 11,
                          marginBottom: 6,
                          fontWeight: 700,
                        }}
                      >
                        {profile.badgeNo}
                      </div>
                    )}

                    {(profile.username || profile.email) && (
                      <div
                        style={{
                          fontSize: 14,
                          color: "#1F2937",
                          textAlign: "center",
                          wordBreak: "break-word",
                        }}
                      >
                        {profile.username || profile.email}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 14,
                      color: "#1F2937",
                      marginBottom: 10,
                      gap: 8,
                    }}
                  >
                    <span>Current Expiry Date</span>
                    <span style={{ fontWeight: 600, color: "#444" }}>
                      {prettyProfileDate(profile.tgExpiryDate)}
                    </span>
                  </div>

                  <Row className="g-2 mt-2">
                    <Col xs={4}>
                      <ActionImageButton
                        image={EDIT_PROFILE_ICON}
                        label={"Edit\nProfile"}
                        onClick={openEditModal}
                      />
                    </Col>
                    <Col xs={4}>
                      <ActionImageButton
                        image={FORGOT_PASSWORD_ICON}
                        label={"Forgot\nPassword"}
                        onClick={() => navigate("/forgotpassword")}
                      />
                    </Col>
                    <Col xs={4}>
                      <ActionImageButton
                        image={RENEWAL_LICENSE_ICON}
                        label={"Update\nExpiry Date"}
                        onClick={openRenewalModal}
                      />
                    </Col>
                  </Row>

                  {loadingProfile && (
                    <div className="text-center mt-3" style={{ fontSize: 12 }}>
                      <Spinner size="sm" className="me-2" />
                      Loading profile...
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card
                style={{
                  background: PANEL_BG,
                  borderRadius: 18,
                  border: "1px solid #E6EAF0",
                }}
              >
                <Card.Body
                  style={{ background: "white", borderRadius: 16, padding: 14 }}
                >
                  <div
                    style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}
                  >
                    Renewal Photo Example
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#1F2937",
                      lineHeight: 1.5,
                      marginBottom: 12,
                    }}
                  >
                    Please prepare a picture of your e-badge or a photo of your
                    physical Tourist Gudie badge before proceeding to update
                    your new license expiry date.
                  </div>

                  <LicensePreviewCard
                    name={profile.fullName}
                    expiryDate={
                      profile.tgExpiryDate || profile.nonLicenceExpiryDate
                    }
                    badgeNo={profile.badgeNo}
                  />
                </Card.Body>
              </Card>
            </div>
          </Col>

          <Col xs={12} md={8} lg={9}>
            <Card
              style={{
                background: PANEL_BG,
                borderRadius: 18,
                marginBottom: 16,
              }}
            >
              <Card.Body
                style={{ background: "white", borderRadius: 16, padding: 14 }}
              >
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 20,
                    color: "#1F2937",
                    marginBottom: 12,
                  }}
                >
                  Dashboard
                </div>

                <Row className="g-3 mb-0">
                  <Col xs={12} md={4}>
                    <ColorStatCard
                      title={`LEARN (${CURRENT_YEAR})`}
                      value={loadingStats ? <Spinner size="sm" /> : stats.learn}
                      subtitle="LEARN submissions"
                      gradient="linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)"
                      icon={<Book size={22} color="#fff" />}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ColorStatCard
                      title={`DO (${CURRENT_YEAR})`}
                      value={loadingStats ? <Spinner size="sm" /> : stats.do}
                      subtitle="DO submissions"
                      gradient="linear-gradient(135deg, #FF6A57 0%, #f88677 100%)"
                      icon={<Bullseye size={22} color="#fff" />}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <ColorStatCard
                      title="COURSES"
                      value={stats.courses}
                      subtitle="Available Courses"
                      gradient="linear-gradient(135deg, #2F80ED 0%, #56CCF2 100%)"
                      icon={<Mortarboard size={22} color="#fff" />}
                    />
                  </Col>
                </Row>

                {error && (
                  <div className="mt-3 text-danger" style={{ fontSize: 13 }}>
                    {error}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card
              style={{
                background: PANEL_BG,
                borderRadius: 18,
                marginBottom: 16,
              }}
            >
              <Card.Body
                style={{
                  background: "white",
                  borderRadius: 16,
                  padding: 18,
                  overflowX: "auto",
                }}
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div>
                    <div
                      style={{
                        fontWeight: 900,
                        fontSize: 20,
                        color: "#1F2937",
                      }}
                    >
                      Update Expiry Date Submission Tracking
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#1F2937", marginTop: 4 }}
                    >
                      Follow your submission progress step by step.
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openExpirySubmissionModal(latestUpdateExpiry)}
                    disabled={loadingUpdateExpiry || !latestUpdateExpiry}
                    style={{ borderRadius: 12, fontWeight: 800 }}
                  >
                    {loadingUpdateExpiry ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Loading
                      </>
                    ) : (
                      <>
                        <Eye className="me-1" />
                        View Submission
                      </>
                    )}
                  </Button>
                </div>

                {updateExpiryError && (
                  <Alert variant="danger" style={{ fontSize: 13 }}>
                    {updateExpiryError}
                  </Alert>
                )}

                <div
                  style={{
                    minWidth: 720,
                    position: "relative",
                    paddingTop: 10,
                    marginLeft: 20,
                    paddingBottom: 70,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  {trackingSteps.map((step, index) => (
                    <TrackingStep
                      key={step.title}
                      title={step.title}
                      date={step.date}
                      icon={step.icon}
                      active={index <= lastActiveIndex}
                      lineActive={index < lastActiveIndex}
                      isLast={index === trackingSteps.length - 1}
                      statusType={rejected ? "rejected" : "normal"}
                    />
                  ))}
                </div>

                {(rejected || cancelled) && latestUpdateExpiry?.remarks && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "12px 14px",
                      background: "#FEF2F2",
                      border: "1px solid #FECACA",
                      borderRadius: 12,
                      color: "#991B1B",
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {/* <strong>Rejected Remark:</strong>{" "} */}
                    <strong>
  {cancelled ? "Cancelled Remark:" : "Rejected Remark:"}
</strong>
                    {latestUpdateExpiry.remarks}
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card style={{ background: PANEL_BG, borderRadius: 18 }}>
              <Card.Body
                style={{ background: "white", borderRadius: 16, padding: 24 }}
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div style={{ fontWeight: 900, fontSize: 20 }}>
                    Submissions
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <InputGroup size="sm" style={{ width: 220 }}>
                      <InputGroup.Text>
                        <CalendarEvent />
                      </InputGroup.Text>
                      <Form.Select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                      >
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </Form.Select>
                    </InputGroup>

                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => loadSubmissions(year)}
                      disabled={loadingLearn || loadingDo}
                      style={{ borderRadius: 12 }}
                    >
                      {loadingLearn || loadingDo ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Refresh
                        </>
                      ) : (
                        "Refresh"
                      )}
                    </Button>
                  </div>
                </div>

                {subError ? (
                  <Alert variant="danger" style={{ fontSize: 13 }}>
                    {subError}
                  </Alert>
                ) : null}

                <Row className="g-3">
                  <Col xs={12} lg={6}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                        padding: "12px 14px",
                        borderRadius: 16,
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 12,
                            background:
                              "linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Book size={18} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 900, color: "#1F2937" }}>
                            LEARN
                          </div>
                          <div style={{ fontSize: 12, color: "#1F2937" }}>
                            LEARN activities
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 28,
                          height: 28,
                          padding: "0 8px",
                          borderRadius: 999,
                          background: "#B45309",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {learnList.length}
                      </span>
                    </div>

                    {loadingLearn ? (
                      <div className="p-3 text-center">
                        <Spinner size="sm" className="me-2" />
                        Loading Learn...
                      </div>
                    ) : learnList.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#1F2937" }}>
                        No LEARN submissions for {year}.
                      </div>
                    ) : (
                      <Row className="g-3">
                        {learnList.map((r, idx) => (
                          <Col xs={12} key={r.learn_id || idx}>
                            <SubmissionCard module="learn" rowData={r} />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Col>

                  <Col xs={12} lg={6}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                        padding: "12px 14px",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 12,
                            background:
                              "linear-gradient(135deg, #FF6A57 0%, #f88677 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Bullseye size={18} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 900, color: "#1F2937" }}>
                            DO
                          </div>
                          <div style={{ fontSize: 12, color: "#1F2937" }}>
                            DO activities
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 28,
                          height: 28,
                          padding: "0 8px",
                          borderRadius: 999,
                          background: "#DC2626",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {doList.length}
                      </span>
                    </div>

                    {loadingDo ? (
                      <div className="p-3 text-center">
                        <Spinner size="sm" className="me-2" />
                        Loading Do...
                      </div>
                    ) : doList.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#1F2937" }}>
                        No DO submissions for {year}.
                      </div>
                    ) : (
                      <Row className="g-3">
                        {doList.map((r, idx) => (
                          <Col xs={12} key={r.do_id || idx}>
                            <SubmissionCard module="do" rowData={r} />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showExpirySubmission}
        onHide={closeExpirySubmissionModal}
        centered
        size="lg"
        backdrop={loadingExpirySubmission || cancellingExpiry ? "static" : true}
        className="custom-font"
      >
        <Modal.Header closeButton={!loadingExpirySubmission && !cancellingExpiry}>
          <Modal.Title style={{ fontWeight: 900 }}>
            Update Expiry Date Submission
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {expirySubmissionError ? (
            <Alert variant="danger" style={{ fontSize: 13 }}>
              {expirySubmissionError}
            </Alert>
          ) : null}

          {loadingExpirySubmission ? (
            <div className="text-center py-4">
              <Spinner className="me-2" />
              Loading submission...
            </div>
          ) : expirySubmission ? (
            <>
              <div
                className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
                style={{
                  padding: "14px 16px",
                  background: "#F8FAFC",
                  border: "1px solid #E5E7EB",
                  borderRadius: 14,
                }}
              >
                <div>
                  <div style={{ fontWeight: 900, color: "#111827" }}>
                    Ref No: {expirySubmission.ref_number || "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#1F2937", marginTop: 3 }}>
                    Submitted: {formatTrackingDate(expirySubmission.createdDate || expirySubmission.uploaded_date)}
                  </div>
                </div>
                <Badge
                  bg={badgeVariantByStatus(expirySubmission.status)}
                  style={{ fontSize: 12, padding: "8px 12px" }}
                >
                  {statusIcon(expirySubmission.status)}
                  {expirySubmission.status || "-"}
                </Badge>
              </div>

              <Row className="g-3">
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>Full Name</div>
                  <div style={{ fontWeight: 800 }}>{expirySubmission.fullname || "-"}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>Badge No</div>
                  <div style={{ fontWeight: 800 }}>{expirySubmission.badge_no || "-"}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>Current Expiry Date</div>
                  <div style={{ fontWeight: 800 }}>{expirySubmission.currExpiryDate || "-"}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>New Expiry Date</div>
                  <div style={{ fontWeight: 800 }}>{expirySubmission.newExpiryDate || "-"}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>Approved Date</div>
                  <div style={{ fontWeight: 800 }}>{formatTrackingDate(expirySubmission.approved_date) || "-"}</div>
                </Col>
                <Col xs={12} md={6}>
                  <div style={{ fontSize: 12, color: "#1F2937" }}>File Size</div>
                  <div style={{ fontWeight: 800 }}>{fileSizeLabel(expirySubmission.file_size)}</div>
                </Col>
              </Row>

              <Card
                className="mt-3"
                style={{ borderRadius: 14, border: "1px solid #E5E7EB" }}
              >
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2" style={{ fontWeight: 900 }}>
                        <FileEarmarkText size={24} />
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 420,
                            display: "inline-block",
                          }}
                          title={expirySubmission.file_name}
                        >
                          {expirySubmission.file_name || "No file"}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#1F2937", marginTop: 4 }}>
                        Uploaded: {formatTrackingDate(expirySubmission.uploaded_date) || "-"}
                      </div>
                    </div>

                    {expiryFileUrl ? (
                      <Button
                        size="sm"
                        variant="outline-success"
                        href={expiryFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ borderRadius: 12, fontWeight: 800 }}
                      >
                        <Eye className="me-1" />
                        Open File
                      </Button>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>

              {expirySubmission.remarks ? (
                <div
                  className="mt-3"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: isRejectedRenewalStatus(expirySubmission.status) ? "#FEF2F2" : "#FFFBEA",
                    border: isRejectedRenewalStatus(expirySubmission.status)
                      ? "1px solid #FECACA"
                      : "1px solid #F3E7A6",
                    color: isRejectedRenewalStatus(expirySubmission.status) ? "#991B1B" : "#16386D",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <strong>Remarks:</strong> {expirySubmission.remarks}
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ fontSize: 13, color: "#1F2937" }}>
              No submission found.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-danger"
            onClick={cancelExpirySubmission}
            disabled={
              loadingExpirySubmission ||
              cancellingExpiry ||
              !expirySubmission ||
              !isUnderReviewRenewalStatus(expirySubmission.status)
            }
          >
            {cancellingExpiry ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cancelling...
              </>
            ) : (
              "Cancel Submission"
            )}
          </Button>

          <Button
            variant="outline-secondary"
            onClick={closeExpirySubmissionModal}
            disabled={loadingExpirySubmission || cancellingExpiry}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDetail}
        onHide={closeDetail}
        centered
        size="lg"
        backdrop={detailLoading ? "static" : true}
        className="custom-font"
      >
        <Modal.Header closeButton={!detailLoading} style={{ border: "none", paddingBottom: 0 }}>
          <Modal.Title
            className="d-flex align-items-center gap-2"
            style={{ fontWeight: 900 }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background:
                  detailModule === "learn"
                    ? "linear-gradient(135deg, #F2994A 0%, #F2C94C 100%)"
                    : "linear-gradient(135deg, #FF6A57 0%, #f88677 100%)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {detailModule === "learn" ? (
                <Book size={17} />
              ) : (
                <Bullseye size={17} />
              )}
            </div>
            {detailModule === "learn" ? "Learn Submission" : "Do Submission"}{" "}
            Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ paddingTop: 12 }}>
          {detailError ? (
            <Alert variant="danger" style={{ fontSize: 13 }}>
              {detailError}
            </Alert>
          ) : null}

          {detailLoading ? (
            <div className="text-center py-5 text-muted">
              <Spinner className="me-2" />
              Loading details...
            </div>
          ) : !detailDocs?.length ? (
            <div className="text-center py-5" style={{ color: "#1F2937" }}>
              <FileEarmarkText size={28} color="#cbd5e1" className="mb-2" />
              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                No documents found
              </div>
              <div style={{ fontSize: 12 }}>
                Nothing was uploaded for this submission.
              </div>
            </div>
          ) : (
            <Row className="g-3">
              {Object.keys(groupDocsByType).map((typeKey) => {
                const docs = groupDocsByType[typeKey] || [];
                const accentColor =
                  detailModule === "learn" ? "#B45309" : "#DC2626";
                return (
                  <Col xs={12} key={typeKey}>
                    <Card
                      style={{
                        borderRadius: 14,
                        border: "1px solid rgba(0,0,0,0.06)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 14px",
                          background: "#F8FAFC",
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 22,
                            height: 22,
                            padding: "0 6px",
                            borderRadius: 999,
                            background: accentColor,
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          {docs.length}
                        </span>
                        <div style={{ fontWeight: 800, fontSize: 13.5, color: "#1F2937" }}>
                          {prettyAttachType(typeKey)}
                        </div>
                      </div>

                      <Card.Body style={{ padding: 12 }}>
                        <Row className="g-2">
                          {docs.map((d) => {
                            const filename = getDocFilename(d);
                            const docKey =
                              d.doc_id || `${filename}-${d.uploaded_date}`;
                            const isViewing = viewingDocKey === docKey;
                            return (
                              <Col xs={12} md={6} key={docKey}>
                                <div
                                  style={{
                                    border: "1px solid #E5E7EB",
                                    borderRadius: 12,
                                    padding: 12,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    alignItems: "flex-start",
                                    transition:
                                      "border-color .15s ease, box-shadow .15s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor =
                                      "#cbd5e1";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 12px rgba(15,23,42,.06)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor =
                                      "#E5E7EB";
                                    e.currentTarget.style.boxShadow = "none";
                                  }}
                                >
                                  <div style={{ minWidth: 0 }}>
                                    <div
                                      className="d-flex align-items-center gap-2"
                                      style={{ fontWeight: 800 }}
                                    >
                                      <span
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: 30,
                                          height: 30,
                                          borderRadius: 8,
                                          background: "#F1F5F9",
                                          color: "#1F2937",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <FileEarmarkText size={16} />
                                      </span>
                                      <span
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: 220,
                                          display: "inline-block",
                                          fontSize: 13.5,
                                        }}
                                        title={filename}
                                      >
                                        {filename || "-"}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 11.5,
                                        color: "#1F2937",
                                        marginTop: 4,
                                        marginLeft: 40,
                                      }}
                                    >
                                      Uploaded: {d.uploaded_date || "-"}
                                    </div>
                                  </div>

                                  <div
                                    className="d-flex flex-column gap-2"
                                    style={{ flexShrink: 0 }}
                                  >
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      onClick={() => viewDoc(d)}
                                      disabled={isViewing}
                                      style={{
                                        borderRadius: 12,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {isViewing ? (
                                        <Spinner
                                          size="sm"
                                          className="me-1"
                                          animation="border"
                                        />
                                      ) : (
                                        <Eye className="me-1" />
                                      )}
                                      View
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-success"
                                      onClick={() => downloadDoc(d)}
                                      style={{
                                        borderRadius: 12,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Download className="me-1" />
                                      Download
                                    </Button>
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer style={{ border: "none" }}>
          <Button
            variant="outline-secondary"
            onClick={closeDetail}
            disabled={detailLoading}
            style={{ borderRadius: 12, minWidth: 100 }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEdit}
        onHide={closeEditModal}
        centered
        backdrop={saving ? "static" : true}
        className="custom-font"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontWeight: 800 }}>Update Profile</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editError && <Alert variant="danger">{editError}</Alert>}
          {editSuccess && <Alert variant="success">{editSuccess}</Alert>}

          <Form>
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: 13, fontWeight: 700 }}>
                    First Name
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <PersonVcard />
                    </InputGroup.Text>
                    <Form.Control
                      value={form.firstName}
                      onChange={onChange("firstName")}
                      disabled={saving}
                      placeholder="Enter first name"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: 13, fontWeight: 700 }}>
                    Last Name
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <PersonVcard />
                    </InputGroup.Text>
                    <Form.Control
                      value={form.lastName}
                      onChange={onChange("lastName")}
                      disabled={saving}
                      placeholder="Enter last name"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 700 }}>
                Email
              </Form.Label>
              <Form.Control
                value={form.username}
                onChange={onChange("username")}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 700 }}>
                Phone
              </Form.Label>
              <InputGroup>
                <Form.Select
                  value={form.countryCode || ""}
                  onChange={onChange("countryCode")}
                  disabled={saving}
                  style={{ maxWidth: 140 }}
                >
                  <option value="">Code</option>
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control
                  value={form.phoneNo}
                  onChange={onChange("phoneNo")}
                  disabled={saving}
                />
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={closeEditModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveProfile} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <LicenseRenewalModal
        show={showRenewal}
        onHide={closeRenewalModal}
        profile={profile}
        authToken={authToken}
        analyzeEndpoint={ENDPOINT_ANALYZE_BADGE}
        submitEndpoint={ENDPOINT_UPDATE_LICENCE}
        onSubmitted={handleRenewalSubmitted}
        editRecord={renewalEditData}
      />
    </div>
  );
}
